import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/* USMedID V70: smoother hover. Opens after 120ms (was 600ms), opens right away when the pointer
   heads down toward the dropdown, switches instantly between tabs, and closes after 300ms. */
const OPEN_DELAY = 120;
const CLOSE_DELAY = 300;
const WARM_WINDOW = 500;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;
  #closeTimer = null;
  #openTimer = null;
  #pendingTarget = null;
  #lastCloseAt = 0;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    this.addEventListener('pointerenter', this.#keepMenuOpen, true);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('pointerdown', this.#onMenuPointerDown);
    document.addEventListener('pointerdown', this.#onOutsidePointer);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#cancelOpen();
    this.#cancelClose();
    this.removeEventListener('pointerenter', this.#keepMenuOpen, true);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('pointerdown', this.#onMenuPointerDown);
    document.removeEventListener('pointerdown', this.#onOutsidePointer);
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => this.#scheduleClose();

  #cancelOpen = () => {
    if (this.#openTimer !== null) clearTimeout(this.#openTimer);
    this.#openTimer = null;
    this.#pendingTarget = null;
  };

  #cancelClose = () => {
    if (this.#closeTimer !== null) clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  };

  #keepMenuOpen = (event) => {
    const activeContainer = this.#state.activeItem?.parentElement;
    if (event.target instanceof Node && activeContainer?.contains(event.target)) this.#cancelClose();
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) this.#cancelClose();
  };

  #scheduleClose = () => {
    this.#cancelClose();
    const item = this.#state.activeItem;
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      if (item?.parentElement?.matches(':hover') || findSubmenu(item)?.matches(':hover')) return;
      this.#deactivate(item);
    }, CLOSE_DELAY);
  };

  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    const hadPendingOpen = this.#openTimer !== null;
    this.#cancelOpen();
    if (!this.#state.activeItem && !hadPendingOpen) return;
    event.preventDefault();
    event.stopPropagation();
    this.#cancelClose();
    this.#state.activeItem?.focus({ preventScroll: true });
    this.#deactivate(this.#state.activeItem, true);
  };

  // Selecting a tab never waits for a pending hover timer or intercepts navigation.
  #onMenuPointerDown = (event) => {
    if (event.button !== 0 || !(event.target instanceof Element)) return;
    const link = event.target.closest('[ref="menuitem"]');
    if (!link || !this.contains(link)) return;
    this.#cancelOpen();
    this.#cancelClose();
    this.#activateNow(link);
  };

  #onOutsidePointer = (event) => {
    if (event.composedPath().includes(this)) return;
    this.#cancelOpen();
    this.#cancelClose();
    this.#deactivate(this.#state.activeItem, true);
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Open mouse-hover menus after a short pause; focus and touch remain immediate.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#cancelClose();
    const target = event.target;
    if (!(target instanceof Element) || !this.headerComponent) return;
    const item = findMenuItem(target);
    if (!item) return;
    if (item === this.#state.activeItem) {
      this.#cancelOpen();
      return;
    }
    const isHover = event.type === 'pointerenter' && event.pointerType !== 'touch';
    // An open (or just-closed) menu switches tabs immediately; a fresh hover waits briefly for intent.
    const isSwitchingOpenMenu = Boolean(this.#state.activeItem && findSubmenu(this.#state.activeItem));
    const isWarm = Date.now() - this.#lastCloseAt < WARM_WINDOW;
    if (!isHover || isSwitchingOpenMenu || isWarm) {
      this.#cancelOpen();
      this.#activateNow(target);
      return;
    }
    if (this.#pendingTarget === target) return;
    this.#cancelOpen();
    this.#pendingTarget = target;
    this.#openTimer = setTimeout(() => {
      this.#cancelOpen();
      if (this.isConnected && target.isConnected) this.#activateNow(target);
    }, OPEN_DELAY);
  };

  #activateNow = (target) => {
    this.#cancelClose();
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = (target.closest('.menu-list__list-item')?.slot ?? target.slot) === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;
    // Leaving a tab downward before its pause ends means the shopper is heading for the dropdown: open it now.
    const pending = this.#pendingTarget;
    if (pending && pending === event.target && event.type === 'pointerleave' && 'clientY' in event) {
      const rect = pending.getBoundingClientRect();
      if (event.clientY >= rect.bottom - 2) {
        this.#cancelOpen();
        this.#activateNow(pending);
        this.#scheduleClose();
        return;
      }
    }
    this.#cancelOpen();
    const container = this.#state.activeItem?.parentElement;
    const next = event.relatedTarget;
    if (next instanceof Node && (container?.contains(next) || this.overflowMenu?.contains(next))) {
      this.#cancelClose();
      return;
    }
    if (event.type === 'blur' || event.type === 'focusout') {
      this.#cancelClose();
      this.#deactivate(this.#state.activeItem, true);
      return;
    }
    this.#scheduleClose();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);
    if (submenu && !force) this.#lastCloseAt = Date.now();

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreItem = element.closest('[slot="more"]');
  if (moreItem) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(moreItem.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) return element;
  return element?.querySelector('[ref="menuitem"]') ?? element.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
